import React from 'react';

export type FormFieldType = 'text' | 'email' | 'password' | 'tel' | 'number' | 'date' | 'textarea';

interface FormFieldProps {
    label: string;
    name: string;
    value: string | number;
    onChange: (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    disabled?: boolean;
    placeholder?: string;
    required?: boolean;
    type?: FormFieldType;
    emptyStateText?: string;
    showEmptyState?: boolean;
    min?: number;
    max?: number;
    step?: number;
    rows?: number;
    className?: string;
    helperText?: string;
    error?: string;
}

/**
 * A more advanced reusable form field component that can handle various input types
 * including text, number, email, password, date, and textarea
 */
const FormField: React.FC<FormFieldProps> = ({
    label,
    name,
    value,
    onChange,
    disabled = false,
    placeholder = '',
    required = false,
    type = 'text',
    emptyStateText = 'No data',
    showEmptyState = false,
    min,
    max,
    step,
    rows = 3,
    className = '',
    helperText,
    error,
}) => {
    const showEmptyStateMessage = !disabled && showEmptyState && !value;
    const fieldClassName = `w-full rounded-md border px-2 text-lg font-semibold py-2 focus:ring-1 focus:outline-none ${
        error ? 'border-red-500 focus:border-red-500' : 'focus:border-blue-500'
    } ${disabled ? 'bg-gray-50 -500' : 'bg-white'} ${className}`;

    return (
        <div className="w-full my-2 px-4">
            <label className="block text-sm font-semibold text-gray-500 mb-1">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </label>

            {showEmptyStateMessage ? (
                <span className="text-gray-600 italic text-sm mb-2">{emptyStateText}</span>
            ) : type === 'textarea' ? (
                <textarea
                    className={fieldClassName}
                    value={value}
                    name={name}
                    disabled={disabled}
                    onChange={onChange}
                    placeholder={placeholder}
                    required={required}
                    rows={rows}
                />
            ) : (
                <input
                    className={fieldClassName}
                    type={type}
                    value={value}
                    name={name}
                    disabled={disabled}
                    onChange={onChange}
                    placeholder={placeholder}
                    required={required}
                    min={min}
                    max={max}
                    step={step}
                />
            )}

            {helperText && !error && <p className="mt-1 text-sm text-gray-500">{helperText}</p>}

            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        </div>
    );
};

export default FormField;
